import moment from "moment";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { utils, write } from "xlsx";
import Papa from "papaparse";
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';

import { DropDownField, TextInputField, CheckboxField } from "../../../helpers";
import Button from "../../../helpers/Button";

import Loading from "../../../common/Loading";
import DateFilter from "../../../common/DateFilter";

import { searchTickets } from '../../../services/api/tickets';
import { fetchTicketList, setSearchConfig, updateSearchOptions, updateSearchParams, setIsSearchByLocked } from "../../../services/redux/reducers/tickets/tickets";
import { fetchTicketCategory, fetchTicketSubCategory, fetchTicketStatus, fetchTicketConstraintCategories } from "../../../services/redux/reducers/commonThunk";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";

import { getAddFilterOptions, getDateString, getDateRange, normalizeLabel, getMatchedRegexPattern, getFullName, sortArrayOfObjectsInAscendingDateOrder } from "../../../utils";


const ticketListSearchConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Ticket List', value: 'ticketList' }
        ]
    },
    {
        srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",
    },
    {
        srNO: 3,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            { label: 'Created Date', value: 'createdAt' },
            { label: 'Due Date', value: 'dueDate' },
        ]
    },
    {
        srNO: 4,
        static: false,
        type: "SELECT",
        name: "status",
        label: "Status",
        options: []
    },
    {
        srNO: 5,
        static: false,
        type: "SELECT",
        name: "priority",
        label: "Priority",
        options: [
            { label: 'Urgent', value: 'Urgent' },
            { label: 'Medium', value: 'Medium' },
            { label: 'Low', value: 'Low' },
            { label: 'High', value: 'High' }
        ]
    },
    {
        srNO: 6,
        static: false,
        type: "SELECT",
        name: "category",
        label: "Category",
        options: [],
    },
    {
        srNO: 7,
        static: false,
        type: "SELECT",
        name: "subcategory",
        label: "Sub Category",
        options: [],
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "titleOrDescription",
        label: "Title/Description",
    },
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "assignee",
        label: "Assignee",
    },
    {
        srNO: 10,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "createdBy",
        label: "Created By",
    },
    {
        srNO: 11,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "ticketId",
        label: "Ticket ID",
        isValid: (text) => /^\d{5,6}$/.test(text)
    },
    {
        srNO: 12,
        static: false,
        removable: true,
        type: "SELECT",
        name: "pmBranchId",
        label: "PM Branch",
        options: []
    }
]

let lastStrCommentMatchKeywords = ['Corrective action', 'Preventive actions', 'Remarks'] // last structured comment
let pendingClosureKeywords = [`to "Pending Closure"`]
let ticketClosureKeywords = ['Ticket has been closed']


export const TicketSearch = () => {
    let [updatedSubCategoryList, setuUpdatedSubCategorylist] = useState([])

    const [ assigneeCheckbox , setAssigneeCheckbox ] = useState(true)
    const [ statusCheckbox , setStatusCheckbox ] = useState(true)
    const [ statusCheckboxDisabled, setStatusCheckboxDisabled ] = useState(false)
    const [ assigneeCheckboxDisabled, setAssigneeCheckboxDisabled ] = useState(false)
    const [ isDataLoading, setIsDataLoading] = useState(false)

    const paginationParams = useSelector(state => state.ticketsData.paginationParams)
    const searchParams = useSelector(state => state.ticketsData.searchParams)
    const searchOptions = useSelector(state => state.ticketsData.searchOptions)
    const isSearchByLocked = useSelector(state => state.ticketsData.isSearchByLocked)
    const userId = useSelector(state => state.userDetails)?.userId

    const ticketCategories = useSelector(state => state.common.ticketCategories)
    const ticketSubCategories = useSelector(state => state.common.ticketSubCategories)
    const ticketStatus = useSelector(state => state.common.ticketStatus)
    const isLoading = useSelector(state => state.ticketsData.loading)
    const activeUserRoleDetails = useSelector((state) => state.userDetails.activeUserRoleDetails)
    let ticketConstraintCategories = useSelector(state => state.common.ticketConstraintCategories)
    const userDetails = useSelector(state => state.userDetails)
    const PMCompanyDetails = useSelector((state) => state.userDetails.PMCompanyDetails)

    const [procmartUser, setProcmartUser] = useState(true)
    const [selectedCategories, setSelectedCategories] = useState([])

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)

    const searchConfig = ticketListSearchConfig

    let categoriesList = []
    if (procmartUser) {
        categoriesList = ticketCategories?.map((category) => {
            const categoryObj = { label: category.categoryName, value: (category.categoryName), categoryId: category.ticketCategoryId }
            return categoryObj
        })
    } else {
        categoriesList = selectedCategories?.map((category) => {
            const categoryObj = { label: category.categoryName, value: (category.categoryName), categoryId: category.ticketCategoryId }
            return categoryObj
        })
    }

    const subCategoriesList = ticketSubCategories?.map((subCategory) => {
        const subCategoryObj = { label: subCategory.subcategoryName, value: (subCategory.subcategoryName), categoryId: subCategory.categoryId }
        return subCategoryObj
    })

    const ticketStatusIdOrder = [1, 10, 8, 9, 2]
    const statusList = ticketStatus
        ?.filter(status => ![3, 4].includes(status.ticketStatusId))
        .sort((a, b) => ticketStatusIdOrder.indexOf(a.ticketStatusId) - ticketStatusIdOrder.indexOf(b.ticketStatusId))
        .map(status => ({ label: status.statusName, value: status.statusName }))

    const dispatch = useDispatch()


    useEffect(() => {
        dispatch(setSearchConfig(ticketListSearchConfig))
        if (searchOptions.length === 0) {
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }

        if (activeUserRoleDetails?.companyId !== PMCompanyDetails.companyId) {
            setProcmartUser(false)
        } else {
            setProcmartUser(true)
        }
    }, [])

    useEffect(() => {
        if (ticketCategories && ticketCategories.length > 0) {
            const relatedCompanyId = activeUserRoleDetails?.companyId;
            const relatedCategoriesId = ticketConstraintCategories?.filter(each => each?.companyId === relatedCompanyId)?.map(each => each.ticketCategoryId);
            const filteredCategories = ticketCategories?.filter(category => relatedCategoriesId?.includes(category.ticketCategoryId));
            setSelectedCategories(filteredCategories);
        }
    }, [ticketCategories, activeUserRoleDetails, ticketConstraintCategories]);


    const addFilterOption = (srNO) => {
        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)

        const updatedConfig = {...relatedConfig}

        if (updatedConfig.name === "category") {
            updatedConfig.options = categoriesList
        }
        else if (updatedConfig.name === "subcategory") {
            updatedConfig.options = updatedSubCategoryList
        }
        else if (updatedConfig.name === "status") {
            setStatusCheckbox(false)
            dispatch(updateSearchParams({ ...searchParams, openStatus: false }))
            setStatusCheckboxDisabled(true)
            updatedConfig.options = statusList
        }
        else if (updatedConfig.name === "pmBranchId") {
            const updatedCompanyBranches = PMCompanyDetails.companyBranches.map(eachBranch => ({
                ...eachBranch,
                label: eachBranch.branchName,
                value: eachBranch.branchId,
            }))
            updatedConfig.options = updatedCompanyBranches
        }

        dispatch(updateSearchOptions([...searchOptions, { ...updatedConfig }]))
    }


    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.name == "status") {
            setStatusCheckboxDisabled(false)
        }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        if (event.target.name == "category") {
            const categoryName = event.target.value
            const category = categoriesList.find(category => category.label == categoryName)
            if (category) {
                updatedSubCategoryList = subCategoriesList.filter(subCategory => subCategory.categoryId == category.categoryId)
                setuUpdatedSubCategorylist(updatedSubCategoryList)
            }
        }

        let eventValue = event.target.value
        const updatedSearchParams = { ...searchParams, [event.target.name]: event.target.value }

        if (event.target.name === "searchText" && event.target.value.length > 3) {
            const availableOptions = [...searchConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            if(!isSearchByLocked){
                updatedSearchParams.searchBy = matchedPattern
            }
        }

        if (event.target.name === "searchBy"){
            dispatch(setIsSearchByLocked(true))
        }


        // if (event.target.name == "searchBy") {
        //     if (event.target.value == "assignee") {
        //         setAssigneeCheckbox(false)
        //         updatedSearchParams.assignToMe = false
        //         setAssigneeCheckboxDisabled(true)
        //     }
        //     else {
        //         if (assigneeCheckboxDisabled) {
        //             setAssigneeCheckboxDisabled(false)
        //         }
        //     }
        // }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleSearchByLock = () => {
        if(isSearchByLocked && searchParams.searchText === ""){
            const updatedSearchParams = { ...searchParams, searchBy : "all" }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        else if(isSearchByLocked && searchParams.searchText !== ""){
            const availableOptions = [...searchConfig]
            const matchedPattern = getMatchedRegexPattern(searchParams.searchText, availableOptions, searchParams.searchBy)
            const updatedSearchParams = { ...searchParams, searchBy : matchedPattern }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        dispatch(setIsSearchByLocked(!isSearchByLocked))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        getDateRange(range)
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)
    
    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSearch = () => {
        let errorFound = false
        
        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })

        if (searchParams.searchText && !searchParams.searchBy) {
            return dispatch(raiseToast({ severity: 'error', detail: "Please select search option!", life: 3000 }))
        }
        
        const updatedSearchParams = { ...searchParams, smartSearch : false }
        dispatch(updateSearchParams({...updatedSearchParams}))

        if (updatedSearchParams.searchBy !== "all") {
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        if (!errorFound) {
            dispatch(fetchTicketList({ ...paginationParams, ...updatedSearchParams, pageNo: 0, userId, procmartUser: procmartUser }))
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadData("CSV")
        }
    ]

    const downloadData = async (dataFormat) => {
        const ticketSearchParams = {
            ...searchParams,
            limitedPagination: false,
            paginator: false,
            userId: userId,
        }
        
        if (ticketSearchParams.searchBy !== "all") {
            ticketSearchParams[ticketSearchParams.searchBy] = ticketSearchParams.searchText.trim()
        }

        setIsDataLoading(true)
        const downloadTicketRes = await searchTickets(ticketSearchParams)
        if(downloadTicketRes.failure){
            setIsDataLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: "Download Ticket Data", detail: downloadTicketRes.data.error}))
        }

        const formattedTicketData = downloadTicketRes.data.data.map(eachTicket => {
            const relatedCategory = ticketCategories.find(eachCategory => eachCategory.ticketCategoryId === eachTicket.categoryId)
            const relatedSubCategory = ticketSubCategories.find(eachSubCategory => eachSubCategory.ticketSubcategoryId === eachTicket.subcategoryId)
            const relatedStatus = ticketStatus.find(eachStatus => eachStatus.ticketStatusId === eachTicket.ticketStatusId)
            const relatedCreatedByUser = PMCompanyDetails.companyUsers.find(eachUser => eachUser.userId === eachTicket.createdBy)
            const relatedAssigneeUser = PMCompanyDetails.companyUsers.find(eachUser => eachUser.userId === eachTicket.assignedTo)
            const relatedPmBranch = PMCompanyDetails.companyPmBranches.find(eachBranch => eachBranch.branchId === eachTicket.pmBranchId)
    
            const formattedTicket = {
                TicketId: eachTicket.ticketId,
                Title: eachTicket.title,
                Description: eachTicket.description,
                CreatedBy: getFullName(relatedCreatedByUser.firstName, relatedCreatedByUser.lastName),
                AssignedTo: getFullName(relatedAssigneeUser.firstName, relatedAssigneeUser.lastName),
                Category: relatedCategory.categoryName,
                SubCategory: relatedSubCategory.subcategoryName,
                Priority: eachTicket.priority,
                Status: relatedStatus.statusName,
                CompanyName: eachTicket.companyName,
                BranchName: eachTicket.branchName,
                Region: relatedPmBranch ? relatedPmBranch.region : "N/A",
                PmBranch: relatedPmBranch ? relatedPmBranch['companyBranch.branchName']: "N/A"
            }
            
            formattedTicket.CreatedAt = getDateString(eachTicket.createdAt, "YYYY-MM-DD")
            formattedTicket.DueDate = getDateString(eachTicket.dueDate, "YYYY-MM-DD")

            if (eachTicket.ticketComments.length > 0) {
                let pendingClosureDate = null
                let ticketClosureDate = null
                let lastStructuredComment = null
            
                eachTicket.ticketComments.forEach(eachComment => {
                    const lowerComment = eachComment.comment.toLowerCase()
                    const commentCreatedAt = new Date(eachComment.createdAt)
            
                    if (!ticketClosureDate && ticketClosureKeywords.some(keyword => lowerComment.includes(keyword.toLowerCase()))) {
                        ticketClosureDate = eachComment.createdAt
                    }
            
                    if (ticketSearchParams.category) {
                        if (pendingClosureKeywords.some(keyword => lowerComment.includes(keyword.toLowerCase()))) {
                            if (!pendingClosureDate || commentCreatedAt > new Date(pendingClosureDate)) {
                                pendingClosureDate = eachComment.createdAt
                            }
                        }
            
                        if (lastStrCommentMatchKeywords.some(keyword => lowerComment.includes(keyword.toLowerCase()))) {
                            if (!lastStructuredComment || commentCreatedAt > new Date(lastStructuredComment.createdAt)) {
                                lastStructuredComment = eachComment
                            }
                        }
                    }
                })
            
                formattedTicket.TicketClosureDate = ticketClosureDate
                    ? getDateString(ticketClosureDate, "YYYY-MM-DD")
                    : null
            
                if (ticketSearchParams.category) {
                    formattedTicket.PendingClosureDate = pendingClosureDate
                        ? getDateString(pendingClosureDate, "YYYY-MM-DD")
                        : null
            
                    formattedTicket.IssueOriginDate = eachTicket.ticketParamValues
                        ? getDateString(eachTicket.ticketParamValues['Issue Origin Date'], "YYYY-MM-DD")
                        : null
            
                    formattedTicket['TimeToPendingClosure (In Days)'] = pendingClosureDate
                        ? (new Date(formattedTicket.PendingClosureDate) - new Date(formattedTicket.CreatedAt)) / (1000 * 3600 * 24)
                        : null
            
                    formattedTicket['TimeToTicketClosure (In Days)'] = ticketClosureDate
                        ? (new Date(formattedTicket.TicketClosureDate) - new Date(formattedTicket.CreatedAt)) / (1000 * 3600 * 24)
                        : null
            
                    if (lastStructuredComment) {
                        const commentText = lastStructuredComment.comment
                        const correctiveMatch = commentText.match(/Corrective action:\s*([^,]*)/i)
                        const preventiveMatch = commentText.match(/Preventive action:\s*([^,]*)/i)
                        const remarksMatch = commentText.match(/Remarks:\s*(.*)$/i)
            
                        formattedTicket.CorrectiveAction = correctiveMatch ? correctiveMatch[1].trim() : 'N/A'
                        formattedTicket.PreventiveAction = preventiveMatch ? preventiveMatch[1].trim() : 'N/A'
                        formattedTicket.Remarks = remarksMatch ? remarksMatch[1].trim() : 'N/A'
                    } else {
                        formattedTicket.CorrectiveAction = 'N/A'
                        formattedTicket.PreventiveAction = 'N/A'
                        formattedTicket.Remarks = 'N/A'
                    }
                }
                const sortedComments = sortArrayOfObjectsInAscendingDateOrder(eachTicket.ticketComments, "createdAt", "DESC")
                const latestComment = sortedComments[0]
                const latestCommentCreator = PMCompanyDetails.companyUsers.find(eachUser => eachUser.userId === latestComment.createdBy)

                formattedTicket.LastUpdatedBy = latestCommentCreator ? getFullName(latestCommentCreator.firstName, latestCommentCreator.lastName) : null
                formattedTicket.LastUpdatedAt = getDateString(latestComment.createdAt, "YYYY-MM-DD HH:mm")
            } else {
                formattedTicket.TicketClosureDate = null
                formattedTicket.LastUpdatedBy = null
                formattedTicket.LastUpdatedAt = null
                if (ticketSearchParams.category) {
                    formattedTicket.PendingClosureDate = null
                    formattedTicket.IssueOriginDate = null
                    formattedTicket['TimeToPendingClosure (In Days)'] = null
                    formattedTicket['TimeToTicketClosure (In Days)'] = null
                    formattedTicket.CorrectiveAction = 'N/A'
                    formattedTicket.PreventiveAction = 'N/A'
                    formattedTicket.Remarks = 'N/A'
                }
            }
            return formattedTicket
        })

        setIsDataLoading(false)

        if (dataFormat === "CSV") {
            let csv = Papa.unparse(formattedTicketData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = "ticket_dump.csv"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if (dataFormat === "XLSX") {
            const ws = utils.json_to_sheet(formattedTicketData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'ticket_dump.xlsx'
            a.click()
            URL.revokeObjectURL(url)
        }
    }


    const handleCheckBoxChange = (event) => {
        if (event.target.name == "assigneeCheckbox") {
            setAssigneeCheckbox(event.checked)
            dispatch(updateSearchParams({ ...searchParams, assignToMe: event.checked }))
        }

        else if (event.target.name == "statusCheckbox") {
            setStatusCheckbox(event.checked)
            dispatch(updateSearchParams({ ...searchParams, openStatus: event.checked }))
        }
    }

    return (
        <div className="p-card" style={{border:"1px solid #e9ecef"}} onKeyDown={handleSearchKeyPress}>
            <Loading visible={isDataLoading} />

            <Toast ref={Toast} />
            <div className="grid">
                {
                    searchOptions.map(eachOption => {
                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{ label: '', value: 'all' }]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if(!optionInUse){
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })

                            return (
                                <>
                                    <div className="flex flex-column col-12">
                                        <TextInputField
                                            name="searchText"
                                            labelText="Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    </div>
                                    {(searchParams.searchText || isSearchByLocked)  && (
                                        <>
                                            <div className="flex justify-content-end p-0 col-12 remove-opt">
                                                <i
                                                    className={`pi ${isSearchByLocked ? 'pi-lock' : 'pi-lock-open'}`}
                                                    style={{ position: 'absolute', top: '6px', cursor: 'pointer', color: 'orange' }}
                                                    onClick={() => handleSearchByLock()}
                                                ></i>
                                            </div>
                                            <div className="flex flex-column col-12">
                                                <DropDownField
                                                    name="searchBy"
                                                    labelText="Search By"
                                                    options={searchByOptions}
                                                    value={searchParams["searchBy"]}
                                                    onChange={handleSearchOnChange}
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            )
                        }

                        if (eachOption.type === "DATE") {
                            return (
                                <>
                                    <DateFilter 
                                        eachOption={eachOption} 
                                        searchParams={searchParams} 
                                        dateOptions={["MTD", "PM", "L15", "L30"]}
                                        handleDateRangeChange={handleDateRangeChange} 
                                        handleSearchOnChange={handleSearchOnChange}
                                        removeFilterOption={removeFilterOption}
                                    />
                                </>
                            )
                        }

                        if (eachOption.type === "SELECT") {
                            const updatedOptions = eachOption.options.map(eachSelectOption => ({ label: eachSelectOption.label, value: eachSelectOption.value }))
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField id={eachOption.categoryId} name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                        </div>
                                    }
                                </>
                            )
                        }
                    })
                }
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-row col-12 justify-content-between">
                    <div>
                        {/* <CheckboxField  name="assigneeCheckbox" onChange={e => setAssigneeCheckbox(e.checked)} checked={ assigneeCheckbox } inputId="checkID" label= "Assigned to me" disabled={assigneeCheckboxDisabled} /> */}
                        {activeUserRoleDetails?.companyId == 1 ? <CheckboxField name="assigneeCheckbox" onChange={e => handleCheckBoxChange(e)} checked={searchParams.assignToMe} inputId="checkID" label="Assigned to me" disabled={assigneeCheckboxDisabled} /> : null}
                    </div>
                    <div className="ml-3">
                        {/* <CheckboxField name="statusCheckbox" onChange={e => setStatusCheckbox(e.checked)} checked={ statusCheckbox } inputId="checkID" label={"Status Open"} disabled={statusCheckboxDisabled} /> */}
                        <CheckboxField name="statusCheckbox" onChange={e => handleCheckBoxChange(e)} checked={searchParams.openStatus} inputId="checkID" label={"Status Open"} disabled={statusCheckboxDisabled} />
                    </div>
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} permissions={[240]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    )
}

export default TicketSearch